import { collection, limit, onSnapshot, orderBy, query } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { useCallback, useEffect, useState } from 'react';
import { db, functions } from '../../lib/firebase';

/**
 * 既存の投稿へのまとめてのぼかし (docs/28 §4-7・§10-3)。
 * ジョブの作成 (callable `startPhotoPrivacyJob`) と、最新のジョブの進捗の購読。
 */
export interface PhotoPrivacyJobProgress {
  id: string;
  status: 'running' | 'done';
  total: number;
  cursor: number;
  published: number;
  failed: number;
  skipped: number;
}

export function usePhotoPrivacyJob(districtId: string | null) {
  const [job, setJob] = useState<PhotoPrivacyJobProgress | null>(null);
  const [starting, setStarting] = useState(false);

  useEffect(() => {
    if (!districtId) return;
    const q = query(
      collection(db, 'districts', districtId, 'photoPrivacyJobs'),
      orderBy('createdAt', 'desc'),
      limit(1),
    );
    return onSnapshot(
      q,
      (snap) => {
        const d = snap.docs[0];
        if (!d) {
          setJob(null);
          return;
        }
        const v = d.data();
        setJob({
          id: d.id,
          status: v.status === 'done' ? 'done' : 'running',
          total: Number(v.total) || 0,
          cursor: Number(v.cursor) || 0,
          published: Number(v.published) || 0,
          failed: Number(v.failed) || 0,
          skipped: Number(v.skipped) || 0,
        });
      },
      () => setJob(null),
    );
  }, [districtId]);

  const start = useCallback(
    async (scope: 'unprocessed' | 'failed' | 'posts', postIds?: string[]) => {
      if (!districtId) return { total: 0 };
      setStarting(true);
      try {
        const fn = httpsCallable<
          { districtId: string; scope: 'unprocessed' | 'failed' | 'posts'; postIds?: string[] },
          { ok: true; jobId: string | null; total: number }
        >(functions, 'startPhotoPrivacyJob');
        const r = await fn({ districtId, scope, ...(postIds ? { postIds } : {}) });
        return { total: r.data.total };
      } finally {
        setStarting(false);
      }
    },
    [districtId],
  );

  return { job, starting, start };
}
